import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ── Iconos inline para no añadir dependencias ─────────────────── */
function IconShare() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}
function IconAdd() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="4" />
      <line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}
function IconDownload() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
      <polyline points="7 10 12 15 17 10"/><line x1="12" y1="3" x2="12" y2="15"/>
    </svg>
  );
}
function IconClose() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}
function IconSpin() {
  return (
    <svg className="animate-spin" width="20" height="20" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83
               M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

/* ── Lógica de dismiss ──────────────────────────────────────────── */
const DISMISS_KEY  = "pwa_install_dismissed_at";
const DISMISS_DAYS = 7;
function wasDismissedRecently() {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  return Date.now() - Number(ts) < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

/* ── Beneficios compartidos ─────────────────────────────────────── */
const BENEFITS = [
  { emoji: "⚡", title: "Acceso instantáneo",           desc: "Ábrela directo desde tu pantalla de inicio" },
  { emoji: "🔔", title: "Notificaciones en tiempo real", desc: "Ofertas, pedidos y descuentos exclusivos" },
  { emoji: "🎁", title: "QPoints exclusivos",            desc: "Gana y canjea puntos en cada compra" },
];

/* ══════════════════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
══════════════════════════════════════════════════════════════════ */
export default function InstalacionPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible,        setVisible]        = useState(false);
  const [platform,       setPlatform]       = useState(null); // 'android' | 'ios'
  const [installing,     setInstalling]     = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    if (standalone || wasDismissedRecently()) return;

    const ua       = navigator.userAgent;
    const isIOS    = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setPlatform("android");
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    if (isIOS && !isAndroid) {
      setPlatform("ios");
      const t = setTimeout(() => setVisible(true), 2200);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", handler); };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") dismiss(); };
    if (visible) window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  const instalar = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
    setInstalling(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        /* ── Backdrop ─────────────────────────────────────── */
        <motion.div
          key="pwa-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.22 }}
          onClick={dismiss}
          style={{
            position: "fixed", inset: 0,
            zIndex: 100000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
            background: "rgba(2, 6, 23, 0.78)",
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
        >
          {/* ── Card ─────────────────────────────────────── */}
          <motion.div
            key="pwa-card"
            initial={{ opacity: 0, scale: 0.88, y: 28 }}
            animate={{ opacity: 1, scale: 1,    y: 0  }}
            exit={{   opacity: 0, scale: 0.94,  y: 16 }}
            transition={{ type: "spring", damping: 26, stiffness: 340, delay: 0.04 }}
            onClick={e => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 400,
              background: "linear-gradient(160deg, #0f172a 0%, #1a1f35 100%)",
              borderRadius: 28,
              border: "1px solid rgba(255,255,255,0.09)",
              boxShadow: "0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(255,255,255,0.04)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Banda de color superior */}
            <div style={{
              height: 4,
              background: "linear-gradient(90deg, #f59e0b, #fbbf24, #fde68a, #f59e0b)",
              backgroundSize: "200% 100%",
            }} />

            {/* Botón cerrar */}
            <button
              onClick={dismiss}
              aria-label="Cerrar"
              style={{
                position: "absolute", top: 16, right: 16,
                width: 32, height: 32,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.1)",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "rgba(255,255,255,0.5)",
                cursor: "pointer",
                transition: "background 0.15s, color 0.15s",
              }}
              onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "white"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
            >
              <IconClose />
            </button>

            <div style={{ padding: "24px 24px 28px" }}>

              {/* ── App identity ─────────────────────────── */}
              <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 22 }}>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <img
                    src="/apple-touch-icon.jpg"
                    alt="Qkiere"
                    style={{
                      width: 40, height: 40,
                      borderRadius: 11,
                      border: "2px solid rgba(255,255,255,0.12)",
                      objectFit: "contain",
                      background: "#000",
                      imageRendering: "auto",
                      boxShadow: "0 6px 20px rgba(245,158,11,0.35)",
                    }}
                  />
                  <div style={{
                    position: "absolute", bottom: -4, right: -4,
                    background: "linear-gradient(135deg,#f59e0b,#fbbf24)",
                    color: "#1c1917",
                    fontSize: 9, fontWeight: 900,
                    padding: "2px 6px",
                    borderRadius: 20,
                    letterSpacing: "0.05em",
                    border: "1.5px solid #0f172a",
                  }}>GRATIS</div>
                </div>
                <div>
                  <h2 style={{
                    color: "white", fontSize: 22, fontWeight: 900,
                    letterSpacing: "-0.5px", lineHeight: 1.1, margin: 0,
                  }}>Qkiere.com</h2>
                  <p style={{ color: "rgba(255,255,255,0.5)", fontSize: 13, marginTop: 4 }}>
                    Instala la app • Es gratis
                  </p>
                </div>
              </div>

              {/* ── Beneficios ────────────────────────────── */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
                {BENEFITS.map(({ emoji, title, desc }) => (
                  <div key={title} style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "rgba(255,255,255,0.05)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: 14, padding: "11px 14px",
                  }}>
                    <span style={{ fontSize: 22, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>
                    <div>
                      <p style={{ color: "white", fontSize: 13, fontWeight: 700, margin: 0 }}>{title}</p>
                      <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, marginTop: 2 }}>{desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ══════════════════════════════════════════
                  ANDROID — botón instalación directa
              ══════════════════════════════════════════ */}
              {platform === "android" && (
                <button
                  onClick={instalar}
                  disabled={installing}
                  style={{
                    width: "100%",
                    padding: "15px 24px",
                    borderRadius: 16,
                    background: installing
                      ? "rgba(245,158,11,0.5)"
                      : "linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)",
                    backgroundSize: "200% 100%",
                    color: "#1c1917",
                    fontSize: 15,
                    fontWeight: 900,
                    border: "none",
                    cursor: installing ? "not-allowed" : "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 10,
                    boxShadow: installing ? "none" : "0 6px 20px rgba(245,158,11,0.45)",
                    transition: "transform 0.12s, box-shadow 0.12s",
                    letterSpacing: "0.01em",
                  }}
                  onMouseEnter={e => { if (!installing) { e.currentTarget.style.transform = "scale(1.02)"; e.currentTarget.style.boxShadow = "0 8px 28px rgba(245,158,11,0.6)"; }}}
                  onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,158,11,0.45)"; }}
                  onMouseDown={e => { if (!installing) e.currentTarget.style.transform = "scale(0.97)"; }}
                >
                  {installing ? <><IconSpin /> Instalando…</> : <><IconDownload /> Instalar Qkiere — Gratis</>}
                </button>
              )}

              {/* ══════════════════════════════════════════
                  iOS — guía paso a paso
              ══════════════════════════════════════════ */}
              {platform === "ios" && (
                <div>
                  <p style={{
                    color: "rgba(255,255,255,0.45)",
                    fontSize: 11,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 12,
                  }}>
                    Cómo instalar en iPhone / iPad
                  </p>

                  {/* Paso 1 */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "rgba(59,130,246,0.12)",
                    border: "1px solid rgba(59,130,246,0.25)",
                    borderRadius: 14, padding: "12px 14px", marginBottom: 8,
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: "#3b82f6",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, color: "white",
                    }}>
                      <IconShare />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: "white", fontSize: 13, fontWeight: 700, margin: 0 }}>
                        Toca el botón <span style={{ color: "#60a5fa" }}>Compartir</span>
                      </p>
                      <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, marginTop: 3 }}>
                        En la barra inferior de Safari ⬇️
                      </p>
                    </div>
                    <div style={{
                      background: "rgba(255,255,255,0.08)", borderRadius: 8,
                      padding: "4px 8px",
                      color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 700,
                      flexShrink: 0,
                    }}>1</div>
                  </div>

                  {/* Conector */}
                  <div style={{ display: "flex", justifyContent: "center", margin: "2px 0 6px" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                      stroke="rgba(255,255,255,0.2)" strokeWidth="2" strokeLinecap="round">
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>

                  {/* Paso 2 */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 14,
                    background: "rgba(34,197,94,0.12)",
                    border: "1px solid rgba(34,197,94,0.25)",
                    borderRadius: 14, padding: "12px 14px",
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: "50%",
                      background: "#22c55e",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      flexShrink: 0, color: "white",
                    }}>
                      <IconAdd />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ color: "white", fontSize: 13, fontWeight: 700, margin: 0 }}>
                        Toca <span style={{ color: "#86efac" }}>"Agregar a inicio"</span>
                      </p>
                      <p style={{ color: "rgba(255,255,255,0.45)", fontSize: 11, marginTop: 3 }}>
                        Desplázate abajo en el menú compartir
                      </p>
                    </div>
                    <div style={{
                      background: "rgba(255,255,255,0.08)", borderRadius: 8,
                      padding: "4px 8px",
                      color: "rgba(255,255,255,0.35)", fontSize: 11, fontWeight: 700,
                      flexShrink: 0,
                    }}>2</div>
                  </div>
                </div>
              )}

              {/* Quizás luego */}
              <button
                onClick={dismiss}
                style={{
                  marginTop: 16,
                  width: "100%",
                  background: "none",
                  border: "none",
                  color: "rgba(255,255,255,0.3)",
                  fontSize: 12,
                  cursor: "pointer",
                  padding: "4px",
                  transition: "color 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.color = "rgba(255,255,255,0.6)"}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.3)"}
              >
                Quizás luego
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
